import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRemoteGet } from '@/hooks/useRemoteGet';
import { Authority, CustomSignallerResourceService, OpenAPI, ProfileOutputDTO } from '@/openapi';
import { AuthContext } from '../AuthContext';

interface AuthContextProviderProps {
	children: React.ReactNode;
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
	// Token management
	const [token, setToken] = useState<string | undefined>();
	const [userIsLoggedIn, setUserIsLoggedIn] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	// User information states
	const [userId, setUserId] = useState<number | undefined>();
	const [email, setEmail] = useState<string | undefined>();
	const [username, setUsername] = useState<string>();
	const [role, setRole] = useState<Authority[] | undefined>();
	const [closedContracts, setClosedContracts] = useState<number | undefined>();
	const logoutTimer = useRef<NodeJS.Timeout | null>(null);

	// Token retrieval and validation
	const calculateRemainingTime = useCallback((expirationTime: number) => {
		const currentTime = new Date().getTime(); // Tempo in millisecondi
		return expirationTime * 1000 - currentTime;
	}, []);

	/**
	 * Se il tempo rimanente è inferiore a 3 secondi (circa 3600 ms) rimuove il token
	 * Altrimenti restituisce il token e il tempo rimanente
	 */
	const retrieveStoredToken = useCallback(() => {
		const storedToken = localStorage.getItem('token');
		const storedExpirationTime = localStorage.getItem('expirationTime');

		if (!storedToken || !storedExpirationTime) return { token: undefined, duration: undefined };

		const expirationTime = parseInt(storedExpirationTime);
		const remainingTime = calculateRemainingTime(expirationTime);

		if (remainingTime <= 3600) {
			localStorage.removeItem('token');
			localStorage.removeItem('expirationTime');
			return { token: undefined, duration: undefined };
		}

		return { token: storedToken, duration: expirationTime };
	}, [calculateRemainingTime]);

	// Fetch user data when token is available
	const userData = useRemoteGet<ProfileOutputDTO>({
		remoteMethod: useCallback(() => {
			if (token) {
				return CustomSignallerResourceService.getUserProfile();
			}
			return undefined;
		}, [token]),
		lazy: false
	});

	const logoutHandler = useCallback(() => {
		setToken(undefined);
		setUserIsLoggedIn(false);
		localStorage.removeItem('token');
		localStorage.removeItem('expirationTime');
		OpenAPI.TOKEN = undefined;

		// Reset user information
		setUserId(undefined);
		setEmail(undefined);
		setUsername(undefined);
		setRole(undefined);
		setClosedContracts(undefined);

		if (logoutTimer.current) {
			clearTimeout(logoutTimer.current);
		}
	}, []);

	/**
	 * Avvia il timer per il logout automatico.
	 * Se è già presente un timer, lo cancella e ne avvia uno nuovo.
	 */
	const startLogoutTimer = useCallback(
		(duration: number) => {
			if (logoutTimer.current) {
				clearTimeout(logoutTimer.current);
			}
			logoutTimer.current = setTimeout(() => {
				logoutHandler();
			}, duration);
		},
		[logoutHandler]
	);

	const loginHandler = useCallback(
		(token: string, expirationTime: string) => {
			setToken(token);
			localStorage.setItem('token', token);
			localStorage.setItem('expirationTime', expirationTime);
			setUserIsLoggedIn(true);
			OpenAPI.TOKEN = token;

			const remainingTime = calculateRemainingTime(parseInt(expirationTime));
			startLogoutTimer(remainingTime);

			// Fetch user profile immediately after login
			userData.fetch();
		},
		[calculateRemainingTime, startLogoutTimer, userData]
	);

	// Update user data when fetched
	useEffect(() => {
		if (userData.data) {
			setUserId(userData.data.userId);
			setEmail(userData.data.email);
			setUsername(userData.data.username);
			setRole(userData.data.role);
			setClosedContracts(userData.data.closedContracts);
		}
	}, [userData.data]);

	// Initialize token on mount
	useEffect(() => {
		const tokenData = retrieveStoredToken();
		if (tokenData.token) {
			setToken(tokenData.token);
			setUserIsLoggedIn(true);
			startLogoutTimer(tokenData.duration);
			OpenAPI.TOKEN = tokenData.token;
			userData.fetch();
		} else {
			// If there's not a token, it's not loading
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (userData.data || userData.error) {
			setIsLoading(false);
		}
	}, [userData.data, userData.error]);

	// Login method for external use
	const login = (token: string) => {
		const decodedPayload = JSON.parse(window.atob(token.split('.')[1]));
		loginHandler(token, decodedPayload.exp);
	};

	const contextValue = useMemo(
		() => ({
			token,
			isLoggedIn: userIsLoggedIn,
			isLoading,
			userId,
			email,
			username,
			role,
			closedContracts,
			login,
			logout: logoutHandler
		}),
		[token, userIsLoggedIn, isLoading, userId, email, username, role, closedContracts]
	);

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
